/** @jsxImportSource @emotion/react */
import { Alert } from 'components/Alert';
import { IUseUploadFormOptions, useUploadForm } from './useUploadForm';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  FormLabel,
  MenuItem,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import Select from 'components/Select';
import ListAltAddIcon from 'components/icons/ListAltAddIcon';
import ListAltReplaceIcon from 'components/icons/ListAltReplaceIcon';
import { usePaths } from 'hooks/usePaths';
import { Link } from 'react-router-dom';
import { UseStyles } from 'types/styles';
import { Controller } from 'react-hook-form';
import { renderGroupedTemplatesMenuItems } from 'utils/component';
import { SelectGroupSeparator } from 'components/SelectGroupSeparator';
import { FEATURE_ADMIN_AREA, isEnabled } from 'utils/feature';
import { PermissionsHelper } from './PermissionsHelper';

export const UploadForm: React.FC<IUseUploadFormOptions> = props => {
  const { formatMessage } = useIntl();
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const {
    submitErrorMessage,
    templates,
    countries,
    adminAreas,
    isTemplatesLoading,
    isCountriesLoading,
    isAdminAreasLoading,
    isReady,
    control,
    formState,
    watchCountryId,
    onSubmit,
    clearSubmitErrorMessage,
    register,
    watch,
  } = useUploadForm(props);
  const paths = usePaths();
  const watchFiles = watch('files');
  const isCountriesEmpty = !isCountriesLoading && countries?.length === 0;

  return (
    <form onSubmit={onSubmit}>
      {submitErrorMessage && (
        <Alert onClose={clearSubmitErrorMessage} severity={'warning'}>
          {submitErrorMessage}
        </Alert>
      )}
      <Typography component="h2" variant="subtitle2" p={2}>
        <FormattedMessage id={'UploadForm.uploadDetails'} defaultMessage={'Upload details'} />
      </Typography>
      <Box p={2} mb={4} css={classes.form}>
        <FormLabel css={classes.label}>
          <FormattedMessage id={'UploadFormPage.template'} defaultMessage={'Template'} />
        </FormLabel>
        <Box>
          <Controller
            name={'typeId'}
            control={control}
            render={({ field: { value, ...restField } }) => {
              return (
                <Select
                  fullWidth
                  placeholder={isTemplatesLoading ? formatMessage({ id: 'common.loading' }) : true}
                  value={value || ''}
                  disabled={isTemplatesLoading}
                  {...restField}
                >
                  {renderGroupedTemplatesMenuItems(templates || [])}
                </Select>
              );
            }}
          />
          {formState.errors.typeId?.message && (
            <FormHelperText error>{formState.errors.typeId?.message}</FormHelperText>
          )}
        </Box>
        <FormLabel css={classes.label}>
          <FormattedMessage id={'UploadFormPage.country'} defaultMessage={'Country'} />
        </FormLabel>
        <Box>
          <Controller
            name={'countryId'}
            control={control}
            render={({ field: { value, ...restField } }) => (
              <Select
                fullWidth
                placeholder={isCountriesLoading ? formatMessage({ id: 'common.loading' }) : true}
                disabled={isCountriesLoading || isCountriesEmpty}
                value={value || ''}
                {...restField}
              >
                {countries?.map(country => (
                  <MenuItem key={country.id} value={country.id}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {formState.errors.countryId?.message && (
            <FormHelperText error>{formState.errors.countryId?.message}</FormHelperText>
          )}
          {isCountriesEmpty && (
            <FormHelperText error>
              <FormattedMessage id={'UploadForm.noCountries'} defaultMessage={'You have no access to countries'} />
            </FormHelperText>
          )}
        </Box>
        {isEnabled(FEATURE_ADMIN_AREA) && (
          <>
            <FormLabel css={[classes.label, classes.withInfo]}>
              <FormattedMessage id={'UploadFormPage.administrativeArea'} defaultMessage={'Administrative area'} />
              <Tooltip
                title={
                  <FormattedMessage
                    id={'UploadFormPage.administrativeLevelTooltip'}
                    defaultMessage={
                      'The applicable administrative level of your plan. For example National, State, Province, Parish etc.'
                    }
                  />
                }
                placement="top-start"
              >
                <InfoOutlinedIcon color="primary" />
              </Tooltip>
            </FormLabel>
            <Box>
              <Controller
                name={'adminAreaId'}
                control={control}
                render={({ field: { value, ...restField } }) => (
                  <Select
                    fullWidth
                    placeholder={isAdminAreasLoading ? formatMessage({ id: 'common.loading' }) : true}
                    disabled={isAdminAreasLoading || !adminAreas?.length}
                    value={value || ''}
                    {...restField}
                  >
                    <MenuItem value="-1">
                      <FormattedMessage id={'common.national'} defaultMessage={'National'} />
                    </MenuItem>
                    {Boolean(adminAreas?.length) && <SelectGroupSeparator />}
                    {adminAreas?.map(adminLevel => (
                      <MenuItem key={adminLevel.id} value={adminLevel.id}>
                        {adminLevel.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {formState.errors.adminAreaId?.message && (
                <FormHelperText error>{formState.errors.adminAreaId?.message}</FormHelperText>
              )}
            </Box>
          </>
        )}
        <FormLabel css={classes.label}>
          <FormattedMessage id={'UploadFormPage.file'} defaultMessage={'File'} />
        </FormLabel>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          {watchFiles?.item(0) && (
            <Box css={classes.file}>
              <ArticleOutlinedIcon color="primary" />
              <div>{watchFiles?.item(0)?.name}</div>
              <CheckCircleIcon css={classes.check} color="success" />
            </Box>
          )}
          <Button
            component="label"
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            endIcon={!!watchFiles?.length ? <ListAltReplaceIcon /> : <ListAltAddIcon />}
            css={classes.uploadBtn}
          >
            {!!watchFiles?.length ? (
              <FormattedMessage id={'UploadFormPage.replaceFile'} defaultMessage={'Replace file'} />
            ) : (
              <FormattedMessage id={'UploadFormPage.chooseFile'} defaultMessage={'Choose file'} />
            )}
            <input
              hidden
              accept="application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel.sheet.macroEnabled.12"
              type="file"
              {...register('files')}
            />
          </Button>
          {formState.errors.files?.message && <FormHelperText error>{formState.errors.files?.message}</FormHelperText>}
        </Box>
      </Box>
      <Divider />
      <Controller
        name={'permission'}
        control={control}
        render={({ field: { onChange } }) => <PermissionsHelper countryId={watchCountryId} onChange={onChange} />}
      />
      <Divider />
      <Box mt={4} display="flex" justifyContent="flex-end" gap={2} css={classes.actions}>
        <Button variant="outlined" size="large" component={Link} to={paths.uploads.generate()} disabled={!isReady}>
          <FormattedMessage id={'UploadFormPage.cancel'} defaultMessage={'Cancel'} />
        </Button>
        <Button
          variant="contained"
          size="large"
          endIcon={<FileUploadOutlinedIcon />}
          disabled={!isReady || !formState.isValid}
          type={'submit'}
        >
          <FormattedMessage id={'UploadFormPage.upload'} defaultMessage={'Upload'} />
        </Button>
      </Box>
    </form>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  form: {
    display: 'inline-grid',
    gridTemplateColumns: 'minmax(200px, auto) 1fr',
    alignItems: 'flex-start',
    gap: 20,
    width: '100%',
  },
  dataInfo: {
    alignItems: 'center',
  },
  label: {
    paddingTop: theme.spacing(2),
  },
  uploadBtn: {
    justifyContent: 'space-between',
    marginLeft: 'auto',
    width: 300,
  },
  withInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  tooltip: {
    background: theme.palette.common.white,
  },
  file: {
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  check: {
    marginInlineStart: 5,
  },
  divider: {
    margin: `${theme.spacing(4)} 0`,
  },
});
